import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../forms/UserForm.css";
import "../forms/EquipmentForm.css";
import "../forms/AmcSchedules.css";
import "../SafetyObservation/safetyObservation.css";
import "../SafetyMeetings/safetyMeetings.css";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import MeetingFormFields from "../SafetyMeetings/MeetingFormFields";
import {
  fetchUserOptions,
  parseApiError,
} from "../SafetyObservation/observationUtils";
import {
  buildMeetingFormData,
  formatDateForInput,
  INITIAL_MEETING_FORM_DATA,
  isExistingMeetingAttachment,
} from "../SafetyMeetings/meetingUtils";

function EditSafetyMeeting({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_MEETING_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [isAttachmentPopupOpen, setIsAttachmentPopupOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const selectedAttendees = useMemo(
    () =>
      userOptions.filter((option) =>
        formData.attendee_ids.includes(option.value),
      ),
    [formData.attendee_ids, userOptions],
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.title.trim()) validationErrors.title = "Title is required.";
    if (!formData.date) validationErrors.date = "Meeting date is required.";

    return validationErrors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleAttendeesChange = (selectedOptions) => {
    const attendeeIds = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];

    setFormData((prevState) => ({
      ...prevState,
      attendee_ids: attendeeIds,
    }));
    setErrors((prevState) => ({
      ...prevState,
      attendee_ids: "",
    }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setAttachments((prevState) => [...prevState, ...selectedFiles]);
    setErrors((prevState) => ({
      ...prevState,
      attachments: "",
    }));
    event.target.value = "";
  };

  const handleDeleteAttachment = (index) => {
    setAttachments((prevState) => {
      const targetAttachment = prevState[index];

      if (isExistingMeetingAttachment(targetAttachment)) {
        setRemovedAttachmentIds((prevIds) =>
          prevIds.includes(targetAttachment.id)
            ? prevIds
            : [...prevIds, targetAttachment.id],
        );
      }

      return prevState.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const handleCancelClick = () => {
    navigate("/safety-meetings");
  };

  const handleSaveClick = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildMeetingFormData(formData, attachments);

      const response = await axios.put(
        `${API_URL}/api/safety/meetings/${meetingId}`,
        payload,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      let warningMessage = "";
      if (removedAttachmentIds.length > 0) {
        try {
          await Promise.all(
            removedAttachmentIds.map((attachmentId) =>
              axios.delete(
                `${API_URL}/api/safety/meetings/attachments/${attachmentId}`,
                {
                  headers: {
                    Authorization: authKey,
                  },
                },
              ),
            ),
          );
        } catch (deleteError) {
          warningMessage = parseApiError(
            deleteError,
            "Meeting updated, but some documents could not be removed",
          );
        }
      }

      showSnackbar(
        warningMessage ||
          response.data?.message ||
          "Meeting updated successfully",
        warningMessage ? "warning" : "success",
      );
      window.setTimeout(() => {
        navigate("/safety-meetings");
      }, 900);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to update meeting"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        setIsFetching(true);
        const authKey = localStorage.getItem("authKey");

        const [usersResponse, meetingResponse] = await Promise.all([
          fetchUserOptions(),
          axios.get(`${API_URL}/api/safety/meetings/${meetingId}`, {
            headers: {
              Authorization: authKey,
            },
          }),
        ]);

        const meeting = meetingResponse.data || {};
        const attendees = Array.isArray(meeting.attendees) ? meeting.attendees : [];
        const mergedUserOptions = [...usersResponse];

        attendees.forEach((attendee) => {
          if (
            attendee?.user_id &&
            !mergedUserOptions.some((option) => option.value === attendee.user_id)
          ) {
            mergedUserOptions.push({
              value: attendee.user_id,
              label: attendee.name || "Selected User",
            });
          }
        });

        setUserOptions(mergedUserOptions);
        setFormData({
          title: meeting.title || "",
          date: formatDateForInput(meeting.date),
          location: meeting.location || "",
          agenda: meeting.agenda || "",
          minutes: meeting.minutes || "",
          attendee_ids: attendees
            .map((attendee) => attendee.user_id)
            .filter(Boolean),
        });
        setAttachments(Array.isArray(meeting.attachments) ? meeting.attachments : []);
        setRemovedAttachmentIds([]);
      } catch (error) {
        showSnackbar(parseApiError(error, "Failed to fetch meeting"), "error");
      } finally {
        setIsFetching(false);
      }
    };

    loadPage();
  }, [meetingId]);

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content meeting-form-wrapper">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Edit Meeting</PageBackTitle>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            {isFetching ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <MeetingFormFields
                  formData={formData}
                  errors={errors}
                  userOptions={userOptions}
                  selectedAttendees={selectedAttendees}
                  onInputChange={handleInputChange}
                  onAttendeesChange={handleAttendeesChange}
                  fileInputRef={fileInputRef}
                  attachments={attachments}
                  isAttachmentPopupOpen={isAttachmentPopupOpen}
                  onUploadClick={handleUploadClick}
                  onFileInputChange={handleFileInputChange}
                  onToggleAttachmentPopup={() =>
                    setIsAttachmentPopupOpen((prevState) => !prevState)
                  }
                  onCloseAttachmentPopup={() => setIsAttachmentPopupOpen(false)}
                  onDeleteAttachment={handleDeleteAttachment}
                />
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading || isFetching}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditSafetyMeeting;
